import { useEffect, useState } from 'react';

import classNames from 'classnames';

import { ThinkingSpinner } from '../ThinkingSpinner';
import type { ThinkingSpinnerSize } from '../ThinkingSpinner';

import styles from './AiLoadingIndicator.module.css';

const defaultPhrases = [
  'Thinking',
  'Pondering',
  'Mulling it over',
  'Crunching numbers',
  'Connecting the dots',
  'Brewing ideas',
  'Noodling on it',
  'Chewing on that',
  'Consulting the oracle',
  'Sifting through data',
  'Hatching a plan',
  'Warming up neurons',
] as const;

const AnimatedEllipsis = () => (
  <span aria-hidden className={styles.ellipsis}>
    …
  </span>
);

export type AiLoadingIndicatorProps = {
  phrases?: readonly string[];
  cycleInterval?: number;
  spinnerSize?: ThinkingSpinnerSize;
  className?: string;
  alignment?: 'start' | 'center';
};

export const AiLoadingIndicator = ({
  phrases = defaultPhrases,
  cycleInterval = 3000,
  spinnerSize = 'medium',
  className,
  alignment = 'start',
}: AiLoadingIndicatorProps) => {
  const activePhrases = phrases.length > 0 ? phrases : defaultPhrases;
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * activePhrases.length),
  );

  useEffect(() => {
    if (activePhrases.length <= 1) return undefined;

    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % activePhrases.length);
    }, cycleInterval);
    return () => clearInterval(id);
  }, [activePhrases.length, cycleInterval]);

  return (
    <div className={classNames(styles.root, className)}>
      <div
        className={classNames(styles.indicator, {
          [styles.centered]: alignment === 'center',
        })}
      >
        <ThinkingSpinner size={spinnerSize} className={styles.icon} />
        <span className={styles.text}>
          {activePhrases[index % activePhrases.length]}
          <AnimatedEllipsis />
        </span>
      </div>
    </div>
  );
};
